import type { ChatEvent } from "@rivets/core";
import { onPersistenceUser, persistenceUserId, persistentStore, userPrefix, PERSIST_BUSTER, type KeyValueStore } from "@/lib/persistence";
import { MAX_RETAINED_CHATS, useChatStore, type ChatStreamState } from "./chatStore";

/**
 * The last 20 chats' events in this browser, with the coverage cursor that
 * makes resuming them exact: a chat opened after a reload paints from here,
 * then subscribes from `cursor` and receives only what it missed.
 *
 * Written behind the store (debounced, off the streaming path), scoped to the
 * signed-in user, and cleared with the rest of the cache on sign-out.
 * Provisional events are never written.
 */

export interface CachedChat {
  buster: string;
  events: ChatEvent[];
  cursor: number;
  oldestLoadedSeq: number | null;
  hasMoreBefore: boolean;
  savedAt: number;
  /** Whose transcript this is, so losing access to the task erases it
   * (`forgetCachedChats`). Absent when the chat's details were not loaded. */
  owner?: ChatOwner;
}

export interface ChatOwner { orgId: string; worktreeId: string }

let ownerOf: (chatId: string) => ChatOwner | undefined = () => undefined;
/** How a chat's organization and task are found when it is written. */
export function setChatOwnerLookup(lookup: (chatId: string) => ChatOwner | undefined): void {
  ownerOf = lookup;
}

/** Newest events kept per chat on disk; older history pages back in. */
export const PERSISTED_EVENTS_PER_CHAT = 1_000;
/** And at most this much of them (serialised length): a tool-heavy chat is
 * cut by size long before its count. */
export const PERSISTED_BYTES_PER_CHAT = 1_000_000;
/** A chat is written once it has been quiet this long — never mid-stream,
 * where every frame makes a new event array and a write per second would
 * serialise the whole window on the main thread over and over. */
export const WRITE_IDLE_MS = 5_000;
/** How long a disk copy is trusted at all. */
export const PERSISTED_CHAT_MAX_AGE_MS = 24 * 60 * 60_000;

function chatKey(userId: string, chatId: string) {
  return `${userPrefix(userId)}chat:${chatId}`;
}
function indexKey(userId: string) {
  return `${userPrefix(userId)}chat-index`;
}

/** The on-disk form of a stream: its newest events and a window that still
 * describes them exactly. Cutting the front makes older history "not loaded",
 * never "covered". */
export function toCachedChat(stream: Pick<ChatStreamState, "events" | "cursor" | "oldestLoadedSeq" | "hasMoreBefore">, now = Date.now(), owner?: ChatOwner): CachedChat {
  // Newest first, until either cap: the count, or the serialised size.
  let keep = 0;
  let bytes = 0;
  for (let index = stream.events.length - 1; index >= 0 && keep < PERSISTED_EVENTS_PER_CHAT; index--) {
    bytes += JSON.stringify(stream.events[index]).length;
    if (bytes > PERSISTED_BYTES_PER_CHAT && keep > 0) break;
    keep += 1;
  }
  const cut = keep < stream.events.length;
  const events = cut ? stream.events.slice(stream.events.length - keep) : stream.events;
  const oldestLoadedSeq = cut ? events[0]!.seq : stream.oldestLoadedSeq;
  return {
    buster: PERSIST_BUSTER,
    events,
    // Holding events past the cursor is fine; a cursor below the kept window
    // is not, so it is clamped up to where the window now starts.
    cursor: oldestLoadedSeq !== null ? Math.max(stream.cursor, oldestLoadedSeq - 1) : stream.cursor,
    oldestLoadedSeq,
    hasMoreBefore: cut || stream.hasMoreBefore,
    savedAt: now,
    ...(owner ? { owner } : {}),
  };
}

/** How long opening a chat waits on its disk copy. Past this the chat loads
 * from the network as if there were none: a hung IndexedDB (WebKit after a
 * resume) must never hold a chat on its skeleton. */
export const CACHED_CHAT_READ_TIMEOUT_MS = 150;

export async function readCachedChat(
  chatId: string,
  kv: KeyValueStore | null = persistentStore(),
  timeoutMs = CACHED_CHAT_READ_TIMEOUT_MS,
): Promise<CachedChat | null> {
  const userId = persistenceUserId();
  if (!kv || !userId) return null;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cached = await Promise.race([
    kv.get<CachedChat>(chatKey(userId, chatId)).catch(() => undefined),
    new Promise<undefined>((resolve) => { timer = setTimeout(() => resolve(undefined), timeoutMs); }),
  ]).finally(() => clearTimeout(timer));
  if (!cached || cached.buster !== PERSIST_BUSTER || !Array.isArray(cached.events)) return null;
  if (Date.now() - cached.savedAt > PERSISTED_CHAT_MAX_AGE_MS) return null;
  return cached;
}

let unsubscribeStore: (() => void) | null = null;
/** One idle timer per chat, so a streaming chat never holds back the others. */
const timers = new Map<string, ReturnType<typeof setTimeout>>();
/** What was last written per chat, by identity, so only changed streams go to disk. */
const written = new Map<string, { events: ChatEvent[]; cursor: number }>();
let index: string[] = [];

function clearTimers() {
  for (const timer of timers.values()) clearTimeout(timer);
  timers.clear();
}

/** Whether the events appended since `previous` end a run: the moment a
 * transcript settles, and worth writing without waiting out the idle delay. */
function endsARun(events: readonly ChatEvent[], previous: readonly ChatEvent[] | undefined): boolean {
  const lastSeq = previous?.[previous.length - 1]?.seq ?? 0;
  for (let position = events.length - 1; position >= 0; position--) {
    const event = events[position]!;
    if (event.seq <= lastSeq) break;
    if (event.payload.kind === "job.status" && ["completed", "failed", "canceled"].includes(event.payload.status)) return true;
  }
  return false;
}

/** Starts (or restarts, for another user) the write-behind. */
export function startChatPersistence(
  userId: string | null,
  kv: KeyValueStore | null = persistentStore(),
  { idleMs = WRITE_IDLE_MS }: { idleMs?: number } = {},
): void {
  unsubscribeStore?.();
  unsubscribeStore = null;
  clearTimers();
  written.clear();
  index = [];
  if (!userId || !kv) return;
  void kv.get<string[]>(indexKey(userId)).then((stored) => {
    if (Array.isArray(stored) && index.length === 0) index = stored.slice(0, MAX_RETAINED_CHATS);
  }).catch(() => undefined);
  const schedule = (chatId: string, delay: number) => {
    const existing = timers.get(chatId);
    if (existing) clearTimeout(existing);
    timers.set(chatId, setTimeout(() => {
      timers.delete(chatId);
      void flushChats(userId, kv, Date.now(), chatId);
    }, delay));
  };
  // Leaving the page writes whatever is still waiting out its idle clock.
  const onPageHide = () => {
    clearTimers();
    void flushChats(userId, kv);
  };
  if (typeof window !== "undefined" && typeof window.addEventListener === "function") window.addEventListener("pagehide", onPageHide);
  const unsubscribe = useChatStore.subscribe((state, previous) => {
    if (state.chats === previous.chats) return;
    for (const [chatId, stream] of Object.entries(state.chats)) {
      const before = previous.chats[chatId];
      if (!stream.loaded || (before && before.events === stream.events && before.cursor === stream.cursor)) continue;
      // Every change restarts the chat's idle clock; the end of a run is
      // written at once.
      schedule(chatId, endsARun(stream.events, before?.events) ? 0 : idleMs);
    }
  });
  unsubscribeStore = () => {
    unsubscribe();
    if (typeof window !== "undefined" && typeof window.removeEventListener === "function") window.removeEventListener("pagehide", onPageHide);
  };
}

/** Writes every loaded stream that changed since its last write, and keeps
 * the on-disk set to the 20 most recently used chats. */
export async function flushChats(userId: string, kv: KeyValueStore, now = Date.now(), only?: string): Promise<void> {
  if (persistenceUserId() !== userId) return;
  const chats = useChatStore.getState().chats;
  // Insertion order is LRU order: newest last.
  const recent = Object.keys(chats).filter((id) => chats[id]!.loaded).reverse();
  const nextIndex = [...recent, ...index.filter((id) => !recent.includes(id))].slice(0, MAX_RETAINED_CHATS);
  const writes: Promise<void>[] = [];
  for (const chatId of only === undefined ? recent : recent.filter((id) => id === only)) {
    const stream = chats[chatId]!;
    const last = written.get(chatId);
    if (last && last.events === stream.events && last.cursor === stream.cursor) continue;
    written.set(chatId, { events: stream.events, cursor: stream.cursor });
    writes.push(kv.set(chatKey(userId, chatId), toCachedChat(stream, now, ownerOf(chatId))));
  }
  for (const evicted of index.filter((id) => !nextIndex.includes(id))) {
    written.delete(evicted);
    writes.push(kv.delete(chatKey(userId, evicted)));
  }
  index = nextIndex;
  writes.push(kv.set(indexKey(userId), nextIndex));
  await Promise.all(writes).catch(() => undefined);
}

/**
 * `access.changed` (review #14): erases this browser's copies of transcripts
 * the member can no longer see — every chat in `lost.chatIds`, every one of
 * `orgId` whose task is in `lost.worktreeIds` or, when the fresh lists are
 * complete (`accessible`), not among them. A copy that does not say whose it
 * is goes as well: it is only a cache, and it cannot be shown to be allowed.
 * Copies of other organizations' chats stay. Returns the chats erased.
 */
export async function forgetCachedChats(
  orgId: string,
  lost: { chatIds: readonly string[]; worktreeIds: readonly string[] },
  accessible: ReadonlySet<string> | null,
  kv: KeyValueStore | null = persistentStore(),
): Promise<string[]> {
  const userId = persistenceUserId();
  if (!kv || !userId) return [];
  const lostChats = new Set(lost.chatIds);
  const lostWorktrees = new Set(lost.worktreeIds);
  const stored = await kv.get<string[]>(indexKey(userId)).catch(() => undefined);
  const candidates = new Set([...index, ...(Array.isArray(stored) ? stored : []), ...lostChats]);
  const erased: string[] = [];
  await Promise.all([...candidates].map(async (chatId) => {
    let drop = lostChats.has(chatId);
    if (!drop) {
      const cached = await kv.get<CachedChat>(chatKey(userId, chatId)).catch(() => undefined);
      if (!cached) return;
      const owner = cached.owner;
      drop = !owner || (owner.orgId === orgId
        && (lostWorktrees.has(owner.worktreeId) || (accessible !== null && !accessible.has(owner.worktreeId))));
    }
    if (!drop) return;
    erased.push(chatId);
    written.delete(chatId);
    await kv.delete(chatKey(userId, chatId)).catch(() => undefined);
  }));
  if (erased.length > 0 && persistenceUserId() === userId) {
    const gone = new Set(erased);
    index = (index.length > 0 ? index : Array.isArray(stored) ? stored : []).filter((id) => !gone.has(id));
    await kv.set(indexKey(userId), index).catch(() => undefined);
  }
  return erased;
}

// Follows the signed-in user: a switch or a sign-out stops writing the old one.
onPersistenceUser((userId) => startChatPersistence(userId));
